/**
 * Generisk like-knap. Én komponent, alle indholdstyper.
 *
 * Bruges nu på guides (Tips & Guides). Samme komponent kan genbruges til
 * opskrifter og produkter senere ved at ændre `contentType`.
 *
 * Adfærd:
 *   - Fyldt hjerte når liket, tomt hjerte når ikke.
 *   - Optimistisk toggle: +1/-1 straks, rollback ved API-fejl.
 *   - Send altid X-Guest-Id (håndteres af axios-interceptor i App.js) OG
 *     Authorization for indloggede — backend prioriterer auth.
 *   - Fire GA4/DB event via eksisterende trackEvent-wrapper.
 *
 * Props:
 *   contentType     ('guide' | 'recipe' | 'product')
 *   contentId       (string — content id, IKKE slug)
 *   initialCount    (number — antal likes ved første render)
 *   initialLiked    (bool — om nuværende bruger/gæst har liket)
 *   contentTitle    (string — bruges som GA4 param)
 *   contentCategory (string — bruges som GA4 param)
 *   size            ('sm' | 'md')
 *   onChange        (optional fn({liked, count}) — invalidér parent cache)
 */
import React, { useState, useCallback } from 'react';
import axios from 'axios';
import { FaHeart, FaRegHeart } from 'react-icons/fa';
import { API } from '../App';
import { useAuth } from '../context/AuthContext';
import { getGuestId } from '../utils/guestId';
import { trackEvent, ANALYTICS_EVENTS } from '../utils/analytics';

export const LikeButton = ({
  contentType = 'guide',
  contentId,
  initialCount = 0,
  initialLiked = false,
  contentTitle = '',
  contentCategory = '',
  size = 'md',
  onChange,
}) => {
  const { user } = useAuth();
  const [liked, setLiked] = useState(!!initialLiked);
  const [count, setCount] = useState(Number(initialCount) || 0);
  const [busy, setBusy] = useState(false);

  const userType = user
    ? (user.role === 'admin' || user.role === 'editor' ? 'admin' : 'member')
    : 'guest';

  const handleClick = useCallback(async (e) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    if (!contentId || busy) return;

    // Optimistisk toggle.
    const willLike = !liked;
    setLiked(willLike);
    setCount((c) => Math.max(0, c + (willLike ? 1 : -1)));
    setBusy(true);

    // Sørg for at gæst-id findes (interceptor sender headeren, men vi
    // trigger opret-hvis-mangler her).
    getGuestId();

    try {
      const { data } = await axios.post(
        `${API}/reactions/${contentType}/${contentId}`,
        { reaction_type: 'like' },
      );
      // Reconcile med DB-sandhed — kan afvige hvis flere devices toggler.
      setLiked(!!data.liked);
      setCount(Number(data.count) || 0);

      trackEvent(
        willLike ? ANALYTICS_EVENTS.GUIDE_LIKE : ANALYTICS_EVENTS.GUIDE_UNLIKE,
        {
          guide_id: contentId,
          guide_title: contentTitle || '',
          guide_category: contentCategory || '',
          user_type: userType,
          liked: !!data.liked,
        },
      );

      onChange?.({ liked: !!data.liked, count: Number(data.count) || 0 });
    } catch (err) {
      // Rollback optimistisk state.
      setLiked(!willLike);
      setCount((c) => Math.max(0, c + (willLike ? -1 : 1)));
      console.error('[LikeButton] toggle failed', err);
    } finally {
      setBusy(false);
    }
  }, [contentId, contentType, liked, busy, userType, contentTitle, contentCategory, onChange]);

  const sizes = {
    sm: {
      wrapper: 'gap-1 px-2 py-1 text-sm',
      icon: 'w-4 h-4',
    },
    md: {
      wrapper: 'gap-2 px-3 py-2 text-sm',
      icon: 'w-4 h-4',
    },
  };
  const s = sizes[size] || sizes.md;

  const label = liked ? 'Fjern like' : 'Like denne guide';

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={busy}
      aria-pressed={liked}
      aria-label={label}
      title={label}
      className={`inline-flex items-center ${s.wrapper} rounded-lg border transition-colors disabled:opacity-60 ${
        liked
          ? 'bg-rose-50 border-rose-200 text-rose-600 hover:bg-rose-100'
          : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'
      }`}
      data-testid={`like-btn-${contentType}-${contentId}`}
      data-liked={liked ? 'true' : 'false'}
    >
      {liked ? <FaHeart className={s.icon} /> : <FaRegHeart className={s.icon} />}
      <span data-testid={`like-count-${contentType}-${contentId}`} className="tabular-nums">{count}</span>
    </button>
  );
};

export default LikeButton;
